import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../services/api';
import './EspacioPublicitario.css';

const ADSENSE_SCRIPT_ID = 'adsbygoogle-js';

function loadAdSenseScript(clientId) {
  if (!clientId || document.getElementById(ADSENSE_SCRIPT_ID)) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = ADSENSE_SCRIPT_ID;
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('No se pudo cargar AdSense'));
    document.head.appendChild(script);
  });
}

const EspacioPublicitario = ({ deporte, className = '' }) => {
  const [payload, setPayload] = useState(null);
  const [adsenseConfig, setAdsenseConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [adIndex, setAdIndex] = useState(0);
  const pushedRef = useRef(false);

  const envClient = process.env.REACT_APP_ADSENSE_CLIENT || process.env.REACT_APP_ADSENSE_CLIENT_ID;
  const envSlot = process.env.REACT_APP_ADSENSE_SLOT || process.env.REACT_APP_ADSENSE_SLOT_BANNER;

  useEffect(() => {
    if (!deporte) {
      setHidden(true);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setHidden(false);
    setPayload(null);
    setAdIndex(0);
    setAdsenseConfig(null);
    pushedRef.current = false;

    axios
      .get(`${getApiUrl()}/public/publicidad`, { params: { deporte } })
      .then((res) => {
        if (!cancelled) {
          setPayload(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHidden(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [deporte]);

  useEffect(() => {
    if (loading || hidden || !payload || payload.origen !== 'google') {
      return undefined;
    }

    let cancelled = false;

    const initGoogle = async () => {
      let clientId = envClient;
      let slotId = envSlot;

      if (!clientId || !slotId) {
        try {
          const configRes = await axios.get(`${getApiUrl()}/public/config`);
          clientId = configRes.data?.adsense_client_id || clientId;
          slotId = configRes.data?.adsense_slot_banner || slotId;
        } catch {
          // fallback silencioso
        }
      }

      if (!clientId || !slotId) {
        if (!cancelled) {
          setHidden(true);
        }
        return;
      }

      if (!cancelled) {
        setAdsenseConfig({ clientId, slotId });
      }

      try {
        await loadAdSenseScript(clientId);
        if (!cancelled && !pushedRef.current) {
          pushedRef.current = true;
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch {
        if (!cancelled) {
          setHidden(true);
        }
      }
    };

    initGoogle();

    return () => {
      cancelled = true;
    };
  }, [loading, hidden, payload, envClient, envSlot]);

  const anunciosCount = Array.isArray(payload?.anuncios) ? payload.anuncios.length : 0;

  useEffect(() => {
    if (anunciosCount < 2) return undefined;
    const timer = window.setInterval(() => {
      setAdIndex((current) => (current + 1) % anunciosCount);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [anunciosCount]);

  if (hidden || !deporte) {
    return null;
  }

  if (loading) {
    return <div className={`espacio-publicitario espacio-publicitario--loading ${className}`} aria-hidden="true" />;
  }

  const anuncios = Array.isArray(payload?.anuncios) && payload.anuncios.length
    ? payload.anuncios
    : (payload?.origen === 'propio' && payload.datos ? [payload.datos] : []);
  const anuncio = anuncios[adIndex] || anuncios[0];

  if (anuncio?.imagen) {
    const { imagen, link, proveedor } = anuncio;
    return (
      <div className={`espacio-publicitario espacio-publicitario--propio ${className}`}>
        <a href={link || undefined} target="_blank" rel="noopener noreferrer sponsored">
          <img src={imagen} alt={proveedor || 'Anuncio'} className="espacio-publicitario-img" />
        </a>
        {anuncios.length > 1 && (
          <div className="espacio-publicitario-dots" role="tablist" aria-label="Anuncios">
            {anuncios.map((item, index) => (
              <button
                key={`${item.imagen}-${index}`}
                type="button"
                className={`espacio-publicitario-dot${index === adIndex ? ' is-active' : ''}`}
                aria-label={`Anuncio ${index + 1}`}
                onClick={() => setAdIndex(index)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (payload?.origen === 'google' && adsenseConfig) {
    return (
      <div className={`espacio-publicitario espacio-publicitario--google ${className}`}>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={adsenseConfig.clientId}
          data-ad-slot={adsenseConfig.slotId}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
    );
  }

  return null;
};

export default EspacioPublicitario;
